var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as Tone from 'tone';
import { AudioComponent } from './AudioComponent';
import { GesturePlayer, AudioWorld, NoiseGenerator } from '@audio/index';
import { Action, AppEvent, AudioStep, uiGestureConfig } from "@/constants";
export class AudioEngine extends AudioComponent {
    constructor() {
        super("AudioEngine");
        this._minimalMode = false;
        this.audioWorlds = [];
        this.voiceOversPlayed = new Map();
        this.createGlobals();
    }
    loadSavedMuteState() {
        this._muted = !!localStorage.getItem("library-muted");
        return this._muted;
    }
    loadVoiceoverState() {
        this._minimalMode = localStorage.getItem("library-voiceover") === "disabled";
        return this._minimalMode;
    }
    loadAudioForWorld(request) {
        // Don't load audio if we are muted, but save the request
        if (this._muted) {
            this.loadRequest = request;
            return;
        }
        this.previousAudioWorld = this.currentAudioWorld;
        // console.log(`
        // ------------------------------------------------------------
        // Load audio for world: ${request.name}
        // Previous world: ${this.previousAudioWorld?.name}
        // ------------------------------------------------------------
        // `);
        // bail if no data supplied
        if (request === null || request === void 0 ? void 0 : request.audioData) {
            const { name, initialScene, autoPlay, audioData } = request;
            // Save a current reference to one world only
            this.currentAudioWorld = new AudioWorld({ name, audioData });
            if (autoPlay) {
                this.enterWorld(name, initialScene);
            }
        }
    }
    playGestureAudio(step) {
        if (this.currentAudioWorld && !this._muted) {
            this.currentAudioWorld.playGestureAudio(step);
        }
    }
    playUIEffect(action) {
        if (this._muted) {
            return;
        }
        // Get a filename from the action
        let sampleName;
        switch (action) {
            case Action.QUIT:
            case Action.BACK:
                sampleName = "hiss";
                break;
            case Action.INFO:
                sampleName = "jingle1";
                break;
            case Action.INFO_CLOSE:
                sampleName = "ghost-call";
                break;
        }
        if (sampleName !== undefined) {
            this.uiPlayer.playSpecific(sampleName);
        }
    }
    createGlobals() {
        this.noiseGenerator = new NoiseGenerator();
        this.uiPlayer = new GesturePlayer({
            path: "ui",
            config: uiGestureConfig,
        });
    }
    enterWorld(name, initialScene = AudioStep.TITLES) {
        this.start();
        if (this.previousAudioWorld) {
            if (this.previousAudioWorld.name !== name) {
                // console.log(`Leaving ${this.previousAudioWorld.name}`);
                // Stop transport if we are going back the library
                const shouldStopTransport = /library/.test(name);
                this.previousAudioWorld.leave(shouldStopTransport);
            }
        }
        if (!this.currentAudioWorld) {
            return;
        }
        // Might have noise
        const { noise } = this.currentAudioWorld;
        this.noiseGenerator[!!noise ? "start" : "stop"](noise);
        // In we go
        const skipVoiceover = this.voiceOversPlayed.get(name) || this._muted || this._minimalMode;
        this.currentAudioWorld.enter({ initialScene, skipVoiceover });
        // Mark the voiceover as played
        this.voiceOversPlayed.set(name, true);
    }
    selectScene(sceneIndex) {
        this.currentAudioWorld.selectScene(sceneIndex);
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Tone.start();
        });
    }
    setMinimalMode(value) {
        this._minimalMode = value;
        // If there is a voiceover playing, stop it
        if (this._minimalMode && this.currentAudioWorld) {
            this.currentAudioWorld.stopVoiceover();
        }
        if (this._minimalMode) {
            localStorage.setItem('library-voiceover', "disabled");
        }
        else {
            localStorage.removeItem('library-voiceover');
        }
    }
    toggleMute() {
        this._muted = !this._muted;
        if (this._muted) {
            localStorage.setItem('library-muted', "muted");
            if (this.currentAudioWorld) {
                this.currentAudioWorld.leave();
                const { noise } = this.currentAudioWorld;
                if (noise) {
                    this.noiseGenerator.stop();
                }
            }
        }
        else {
            localStorage.removeItem('library-muted');
            if (this.loadRequest) {
                this.loadAudioForWorld(this.loadRequest);
                const { audioData } = this.loadRequest;
                if (audioData.noise) {
                    this.noiseGenerator.start(audioData.noise);
                }
            }
        }
        this.emit(AppEvent.SOUND_MUTED, this._muted);
        return this._muted;
    }
    get isLibraryWorld() {
        if (!this.currentAudioWorld) {
            return false;
        }
        return /library/.test(this.currentAudioWorld.name);
    }
    get currentSceneIndex() {
        var _a;
        return (_a = this.currentAudioWorld) === null || _a === void 0 ? void 0 : _a.sceneIndex;
    }
    get muted() {
        return this._muted;
    }
    get running() {
        return Tone.Transport.state === "started";
    }
}
